import { useQuery } from "@tanstack/react-query";
import type React from "react";
import AppBar from "../../../components/atoms/appbar";
import Button from "../../../components/atoms/button";
import { useNavigate, useParams } from "react-router-dom";
import { useModalStore } from "../../../lib/store/useModalStore";
import { formatDate, formatDateRange } from "../../../lib/utils/dateUtils";
import dayjs from "dayjs";
import { staticURL } from "../../../lib/utils/imageUtils";
import { parseBirth } from "../../../lib/utils/birthdayUtils";
import { useSettingTournamentApplyDetail } from "../../../@apis/hooks/useSetting";
import { toast } from "react-toastify";
import { useState } from "react";
import { createPortal } from "react-dom";
import CompetitionDrawer from "../../../components/atoms/drawer/CompetitionDrawer";
import type {
  TournamentApplyDetailResponse,
  TournamentDetailResponse,
  TournamentQrListItemResponse,
} from "../../../@apis/dto/response/torunament.response";
import type { RoundItem } from "../../../@apis/dto/response/score.response";
import type { TournamentQrParams } from "../../../@apis/dto/request/tournament.request";
import { useDeviceStore } from "../../../lib/store/useDeviceStore";
import TournamentQrModal from "../../../components/atoms/modal/TournamentQrModal";
import TournamentQrResultModal from "../../../components/atoms/modal/TournamentQrResultModal";
import { useApplyQrInfo } from "../../../components/atoms/modal/ApplyQrInfoCard";
import {
  useTournamentQrCode,
  useTournamentQrList,
} from "../../../@apis/hooks/useTournament";
import { useGetRoundList } from "../../../@apis/hooks/useScore";

const getQrButtonLabel = (qrLabel: string) => `${qrLabel} 인증 QR`;
const getQrResultTitle = (qrLabel: string) => `${qrLabel} 인증 완료`;

// QR 인증 대상과 그 진행 상태.
//  - 라운드 대회: 아직 종료되지 않은 가장 앞 라운드. 모두 끝났으면 마지막 라운드(=최종 종료)
//    (접수 상세의 currentRoundId는 라운드가 시작된 뒤에만 내려오므로 라운드 목록으로 판단한다)
//  - 그 외: 내가 신청한 일정
// 화면 표시와 버튼 클릭 시 재확인이 같은 기준을 쓰도록 분리해 둔다.
const getQrTarget = (
  detail?: TournamentApplyDetailResponse,
  roundList?: RoundItem[],
): { params: TournamentQrParams | null; status?: string } => {
  const tournament = detail?.tournamentInfo;
  const apply = tournament?.apply;

  if (tournament?.tournamentType === "MULTI_ROUND") {
    const rounds = [...(roundList ?? [])].sort(
      (a, b) => a.roundOrder - b.roundOrder,
    );
    const targetRound =
      rounds.find((round) => round.status !== "END") ?? rounds.at(-1);
    return {
      params: targetRound ? { roundId: targetRound.id } : null,
      status: targetRound?.status,
    };
  }

  const appliedCalendar =
    tournament?.calendarInfo?.id === apply?.calendarId
      ? tournament?.calendarInfo
      : tournament?.calendarList?.find(
          (calendar) => calendar.id === apply?.calendarId,
        );
  return {
    params: apply?.calendarId != null ? { calendarId: apply.calendarId } : null,
    status: appliedCalendar?.calendarStatus,
  };
};

// 라벨(회색 w-80) + 값(검정) 한 줄
function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-[8px] items-start text-sm font-medium">
      <p className="min-w-[80px] shrink-0 text-[#8e8e93]">{label}</p>
      {/* 이메일처럼 공백 없는 긴 값도 줄바꿈되도록 min-w-0 + 단어 내 줄바꿈 허용 */}
      <div className="flex-1 min-w-0 text-black break-keep whitespace-pre-line [overflow-wrap:anywhere]">
        {value}
      </div>
    </div>
  );
}

// 상태 뱃지
function Badge({
  label,
  className,
  hasCheck = false,
}: {
  label: string;
  className: string;
  hasCheck?: boolean;
}) {
  return (
    <span
      className={`px-[10px] py-[2.5px] inline-flex items-center gap-[5px] rounded-[5px] text-xs font-medium ${className}`}
    >
      {hasCheck && (
        <svg
          width="0.75rem"
          height="0.75rem"
          viewBox="0 0 16 16"
          fill="none"
          className="shrink-0"
        >
          <path
            d="M13.3307 4L5.9974 11.3333L2.66406 8"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {label}
    </span>
  );
}

// 섹션 카드(흰 배경 + 회색 테두리)
function SectionCard({
  title,
  titleRight,
  children,
}: {
  title?: string;
  titleRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-[#e5e5ea] rounded-[10px] px-[16px] py-[20px] flex flex-col gap-[20px]">
      {(title || titleRight) && (
        <div className="flex items-center justify-between gap-[8px]">
          {title && (
            <p className="text-base font-semibold text-black">{title}</p>
          )}
          {titleRight}
        </div>
      )}
      {children}
    </div>
  );
}

// 접수상태(myStampLabel) 뱃지 정의
const stampBadge = (label?: string | null) => {
  switch (label) {
    case "접수완료":
      return {
        label,
        className: "bg-[#f2ffec] text-[#34c759]",
        hasCheck: true,
      };
    case "승인대기":
    case "결제대기":
      return {
        label,
        className: "bg-[#fff4d8] text-[#a2845e]",
        hasCheck: false,
      };
    case "참가완료":
      return { label, className: "bg-[#8e8e93] text-white", hasCheck: true };
    case "미입금 마감":
      return {
        label,
        className: "bg-[#ffecec] text-[#ff3b30]",
        hasCheck: false,
      };
    case "접수취소":
      return { label, className: "bg-[#f2f2f7] text-black", hasCheck: false };
    default:
      return { label, className: "bg-[#8e8e93] text-white", hasCheck: false };
  }
};

// 결제상태 뱃지 정의
const paymentBadge = (label: string) => {
  switch (label) {
    case "결제실패":
    case "결제미완료":
      return { label, className: "bg-[#ffecec] text-[#ff3b30]" };
    case "결제완료":
      return { label, className: "bg-[#f2ffec] text-[#34c759]" };
    case "결제취소":
      return { label, className: "bg-[#f2f2f7] text-black" };
    default:
      return { label, className: "bg-[#fff4d8] text-[#a2845e]" };
  }
};

// 계좌번호 마스킹: 앞 3자리·뒤 2자리만 노출, 중간은 * 처리
function maskAccountNo(no?: string | null): string {
  if (!no) return "-";
  const digits = no.replace(/\s/g, "");
  if (digits.length <= 5) return "*".repeat(digits.length);
  const head = digits.slice(0, 3);
  const tail = digits.slice(-2);
  return `${head}${"*".repeat(digits.length - 5)}${tail}`;
}

// 예금주명 마스킹: 가운데 글자만 * 처리 (2글자는 마지막 글자)
function maskHolder(name?: string | null): string {
  if (!name) return "-";
  const chars = [...name.trim()];
  if (chars.length <= 1) return name;
  if (chars.length === 2) return `${chars[0]}*`;
  return `${chars[0]}${"*".repeat(chars.length - 2)}${chars[chars.length - 1]}`;
}

// 접수/결제 상세와 취소/환불 상세를 겸한다.
// 접수가 취소되면(isCanceled) 취소·환불 정보를 노출하고, 장소·조편성·하단 액션은 감춘다.
export default function CompetitionHistoryDetail() {
  const navigate = useNavigate();
  const { id, applyId } = useParams();
  const { openModal } = useModalStore();
  const { deviceType } = useDeviceStore();
  const [editOpen, setEditOpen] = useState(false);
  const [qrModal, setQrModal] = useState<{
    qrId: number;
    title: string;
    code: string;
  } | null>(null);
  const [qrResultTitle, setQrResultTitle] = useState<string | null>(null);
  const [isCheckingQr, setIsCheckingQr] = useState(false);

  const { data: tournamentInfo, refetch } = useQuery({
    ...useSettingTournamentApplyDetail(id!, applyId!),
    enabled: !!id && !!applyId,
  });
  const tournament = tournamentInfo?.data?.tournamentInfo;
  const user = tournamentInfo?.data?.userInfo;
  const apply = tournament?.apply;
  const paymentInfo = tournamentInfo?.data?.paymentInfo;
  const assignments = tournamentInfo?.data?.assignments ?? [];
  // 라운드 대회는 일정 대신 라운드로 구분되므로 대회일정을 노출하지 않는다.
  const isRoundTournament = tournament?.tournamentType === "MULTI_ROUND";

  const isPaid = tournament?.isPayed === true;
  const isOnline = tournament?.paymentMethod === "online";

  // 참가상태 뱃지
  // const participation = (() => {
  //   switch (apply?.participationStatus) {
  //     case "PARTICIPATED":
  //       return { label: "참가완료", className: "bg-[#e5f1fd] text-[#007aff]" };
  //     case "CUTOFF":
  //       return { label: "컷오프", className: "bg-[#f2f2f7] text-black" };
  //     case "DISQUALIFIED":
  //       return { label: "실격", className: "bg-[#ffecec] text-[#ff3b30]" };
  //     default:
  //       return { label: "참가전", className: "bg-[#f2f2f7] text-black" };
  //   }
  // })();

  // 접수일시
  const applyDateTime = apply?.insertDt
    ? formatDate(apply.insertDt, "YYYY-MM-DD (ddd) HH:mm:ss")
    : "-";

  // 접수취소 상태 및 취소일시
  const isCanceled =
    tournament?.myStampLabel === "접수취소" || !!apply?.cancelDt;
  const canceledDateTime = apply?.cancelDt
    ? formatDate(apply.cancelDt, "YYYY-MM-DD (ddd) HH:mm:ss")
    : "-";
  const { data: roundListInfo } = useQuery({
    ...useGetRoundList(isRoundTournament ? id : null),
    enabled: isRoundTournament && !!id,
  });

  const qrTarget = getQrTarget(
    tournamentInfo?.data,
    roundListInfo?.data?.roundList,
  );
  const qrParams = qrTarget.params;

  // QR / 인증 완료 화면에 넘길 표시값 (접수 상세와 같은 조회를 재사용, QR 대상 라운드의 편성 기준)
  const { rows: qrInfoRows } = useApplyQrInfo(id!, applyId!, qrParams);

  // 화면에 표기되는 접수/대회 상태 (취소건은 항상 접수취소로 표기)
  const statusLabel = isCanceled
    ? "접수취소"
    : (tournament?.myStampLabel ?? tournament?.tournamentLabel);

  // QR 인증은 접수완료 / 참가완료 상태에서만 노출한다.
  const canQueryQrList =
    statusLabel === "접수완료" || statusLabel === "참가완료";

  const { data: qrListInfo, refetch: refetchQrList } = useQuery({
    ...useTournamentQrList(id!, qrParams),
    enabled: canQueryQrList && !!id && qrParams != null,
  });
  const qrList = [...(qrListInfo?.data ?? [])].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );
  const qrCodeMutation = useTournamentQrCode(id!, qrParams);

  // 선택한 체크인 지점의 실제 QR 코드를 발급받아 풀모달로 표시한다.
  // 화면을 연 뒤 대회가 시작될 수 있으므로 클릭 시점에 대회 상태를 다시 확인하고,
  // 시작 전이면 QR을 발급하지 않는다. 이미 인증이 끝난 지점이면 완료 화면을 바로 보여준다.
  const openQrModal = async (qr: TournamentQrListItemResponse) => {
    // const [refreshedDetail, refreshedRounds] = await Promise.all([
    //   refetch(),
    //   isRoundTournament ? refetchRoundList() : Promise.resolve(null),
    // ]);

    // const refreshedTarget = getQrTarget(
    //   refreshedDetail.data?.data ?? tournamentInfo?.data,
    //   refreshedRounds?.data?.data?.roundList ?? roundListInfo?.data?.roundList,
    // );
    // if (refreshedTarget.status === "PRE_START") {
    //   openModal("ALERT_DIALOG", {
    //     title: "QR 생성 불가",
    //     content: "아직 대회 시작 전입니다.\n대회가 시작되면 QR이 생성됩니다.",
    //   });
    //   return;
    // }

    if (qr.isCheckedIn) {
      setQrResultTitle(getQrResultTitle(qr.qrLabel));
      return;
    }

    try {
      const response = await qrCodeMutation.mutateAsync(qr.qrId);
      if (!response.success || !response.data?.qrCode) {
        throw new Error("QR code was not returned.");
      }
      setQrModal({
        qrId: qr.qrId,
        title: getQrButtonLabel(qr.qrLabel),
        code: response.data.qrCode,
      });
    } catch {
      toast("QR 코드를 불러오지 못했습니다.");
    }
  };

  // QR 화면을 닫을 때 목록을 갱신하고 서버에서 인증 완료가 확인되면 완료 화면을 연다.
  const closeQrModal = async () => {
    if (isCheckingQr) return;
    const closing = qrModal;
    if (!closing) return;

    setIsCheckingQr(true);
    try {
      const refreshed = await refetchQrList();
      if (refreshed.isError) {
        toast("QR 인증 여부를 확인하지 못했습니다. 다시 시도해 주세요.");
        return;
      }

      const checkedInQr = refreshed.data?.data?.find(
        (qr) => qr.qrId === closing.qrId && qr.isCheckedIn,
      );
      setQrModal(null);
      if (checkedInQr) {
        setQrResultTitle(getQrResultTitle(checkedInQr.qrLabel));
      }
    } finally {
      setIsCheckingQr(false);
    }
  };

  // 결제 정보 노출 조건
  //  - 취소건: 결제 이력이 있을 때만 (결제 전 취소는 미노출, 취소 후에는 결제상태가 바뀌므로 isPayed로 판단하지 않는다)
  const showPaymentSection =
    (tournament?.entryFee ?? 0) > 0 && (isCanceled ? !!paymentInfo : true);

  // 접수상태 뱃지
  const stamp = stampBadge(statusLabel);

  const isCard = paymentInfo?.payMethod === "CARD";
  const isPaymentFailed =
    paymentInfo?.paymentStatusLabel === "결제실패" ||
    paymentInfo?.paymentStatusLabel === "결제미완료";

  // 카드 결제 실패로 취소된 건은 취소사유에 결제실패사유를 노출한다.
  const cancelReason = (apply?.cancelReason ?? paymentInfo?.failReason) || "-";

  // 환불 정보: 취소건 중 환불 이력이 있을 때만 노출
  const hasRefund =
    isCanceled &&
    !!paymentInfo &&
    (!!paymentInfo.refundAt || !!paymentInfo.refundStatusLabel);
  const isRefundDone = paymentInfo?.refundStatusLabel === "환불완료";
  const isRefundFailed = paymentInfo?.refundStatusLabel === "환불실패";
  // 환불 계좌(가상계좌/무통장): 계좌번호는 마스킹해 노출
  const refundAccount =
    paymentInfo?.refundBankName || paymentInfo?.refundAccountNo
      ? `${paymentInfo?.refundBankName ?? ""} ${maskAccountNo(paymentInfo?.refundAccountNo)}`.trim()
      : "-";

  const failedAt = paymentInfo?.failedAt ?? paymentInfo?.updateDt;

  // 접수 기간 내 여부: 재접수 대상자는 재접수 기간, 그 외는 일반 접수 기간(시작~종료)
  const applyStartTimestamp = tournament?.isReapply
    ? tournament?.reapplyStartDtTimestamp
    : tournament?.applyStartDtTimestamp;
  const applyEndTimestamp = tournament?.isReapply
    ? tournament?.reapplyEndDtTimestamp
    : tournament?.applyEndDtTimestamp;
  // 신청 회차에 회차별 접수 마감일시(applyEndDt)가 설정돼 있으면 그 마감을 우선 적용한다
  // (서버의 취소/수정 차단과 같은 기준 — 재접수 여부 무관). 미설정(null)이면 기존 대회 공통 기간으로 판정.
  const appliedCalendar =
    tournament?.calendarInfo?.id === apply?.calendarId
      ? tournament?.calendarInfo
      : tournament?.calendarList?.find(
          (calendar) => calendar.id === apply?.calendarId,
        );
  const calendarApplyEndDt = appliedCalendar?.applyEndDt;
  const isWithinApplyPeriod = calendarApplyEndDt
    ? !dayjs().isAfter(dayjs(calendarApplyEndDt))
    : applyStartTimestamp != null &&
      applyEndTimestamp != null &&
      !dayjs().isBefore(dayjs(applyStartTimestamp)) &&
      !dayjs().isAfter(dayjs(applyEndTimestamp));

  const stampLabel = tournament?.myStampLabel;
  const isTransferTournament = tournament?.paymentMethod === "transfer";

  // 접수 수정: 접수 기간 내 + 승인대기/결제대기/접수완료
  const canEditApply =
    !isCanceled &&
    isWithinApplyPeriod &&
    (stampLabel === "승인대기" ||
      stampLabel === "결제대기" ||
      stampLabel === "접수완료");

  // 가상계좌 입금대기 상태: 입금 전이라 취소할 결제가 없으므로 접수 취소 불가
  const isVbankPaymentWait =
    (paymentInfo?.payMethod === "VBANK" || !!paymentInfo?.vBankNum) &&
    stampLabel === "결제대기";

  // 접수 취소: 접수 기간 내 + 상태
  //  - 무통장입금: 승인대기/결제대기
  //  - 온라인(카드/가상계좌): 앱 환불 가능(isRefundApp) && 결제상태가 결제대기/결제완료
  const canCancelApply =
    !isCanceled &&
    !isVbankPaymentWait &&
    isWithinApplyPeriod &&
    (isTransferTournament
      ? stampLabel === "승인대기" || stampLabel === "결제대기"
      : tournament?.isRefundApp === true &&
        (paymentInfo?.paymentStatusLabel === "결제대기" ||
          paymentInfo?.paymentStatusLabel === "결제완료"));

  // 접수 취소
  //  - 결제 전(결제대기) 또는 무통장입금: 취소 사유 모달 → 취소 API
  //  - 온라인 결제완료: 환불 안내가 포함된 접수 취소 페이지로 이동
  const handleCancelApply = () => {
    const isOnlinePaid =
      !isTransferTournament && paymentInfo?.paymentStatusLabel === "결제완료";
    if (isOnlinePaid) {
      navigate(`/setting/tournament-history/${id}/${applyId}/cancel`);
      return;
    }
    openModal("TOURNAMENT_CANCEL_DIALOG", {
      tournamentId: tournament?.id,
      applyId: apply?.id ?? applyId,
      isVbank: paymentInfo?.payMethod === "VBANK" || !!paymentInfo?.vBankNum,
      onSuccess: () => {
        toast("접수가 취소되었습니다.");
        // 같은 화면이 취소/환불 상세로 전환되므로 재조회만 한다.
        refetch();
      },
    });
  };

  // 접수 수정: 현재 페이지 위에 접수 수정 화면(풀 팝업, 약관 다음 단계부터 · 기존 정보 프리필)을 연다
  const handleEditApply = () => setEditOpen(true);

  // 대회 순위 보기: 스코어카드가 있고 참가완료 상태일 때만
  const showRankingButton =
    tournamentInfo?.data?.scoreCardId != null && stampLabel === "참가완료";

  // 하단 고정 액션 노출 여부 (없으면 하단 여백을 줄인다)
  const hasBottomAction = canCancelApply || canEditApply || showRankingButton;

  // 결제완료일시 / 결제금액
  const paidAt =
    paymentInfo?.paidAt ?? (isOnline ? paymentInfo?.updateDt : apply?.updateDt);

  // 결제 완료 상세 노출 조건
  //  - 접수중: 카드 결제 정보가 있을 때 (결제상태 기준)
  //  - 취소건: 결제상태가 이미 바뀌므로 결제완료 이력(paidAt) 기준, 단 가상계좌 환불건은 미노출
  const hasPaidDetail = isCanceled
    ? !isPaymentFailed && !!paymentInfo?.paidAt && !(hasRefund && !isCard)
    : isPaid && !!paymentInfo?.cardName;

  // 결제대기(가상계좌/무통장입금) 입금 안내 정보
  const isDepositWait = tournament?.myStampLabel === "결제대기";
  const isVbank = paymentInfo?.payMethod === "VBANK" || !!paymentInfo?.vBankNum;
  const isTransfer = tournament?.paymentMethod === "transfer";
  const isVbankWait = isDepositWait && isVbank;
  // 결제수단이 가상계좌/무통장입금이고 결제대기 상태일 때 입금 안내 노출
  const showDepositGuide = isDepositWait && (isVbank || isTransfer);
  const depositAmount = paymentInfo?.amt ?? tournament?.entryFee ?? 0;
  const depositDeadline = paymentInfo?.vBankExpDt
    ? formatDate(paymentInfo.vBankExpDt, "YYYY-MM-DD (ddd) HH:mm")
    : "-";
  const depositDeadlineShort = paymentInfo?.vBankExpDt
    ? formatDate(paymentInfo.vBankExpDt, "M월 D일 HH:mm")
    : null;
  // 입금 계좌: 가상계좌는 발급 계좌, 무통장입금은 대회 계좌 목록
  const depositAccounts = isVbank
    ? [
        {
          bankName: paymentInfo?.vBankBankName ?? "",
          accountNumber: paymentInfo?.vBankNum ?? "",
          accountHolder: paymentInfo?.vBankAccountHolder ?? "",
          label: "가상계좌",
        },
      ]
    : (tournament?.accounts ?? [])
        .filter((a) => a.isUse !== false)
        .map((a) => ({
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          accountHolder: a.accountHolder,
          label: a.label,
        }));

  // 클래스 라벨
  const classLabel =
    apply?.applyClass === "rising"
      ? "라이징 클래스"
      : apply?.applyClass === "master"
        ? "마스터 클래스"
        : null;

  const region = apply?.regionName;

  // 대회 설정에서 활성화한 접수 항목만 노출
  const collectRegion = tournament?.isCollectRegion === true;
  const collectCertificate = tournament?.isCertificateAccept !== false;
  const collectClass = tournament?.hasClass === true;

  // 앱에서는 지도 앱 스킴을 실행하고, 웹에서는 웹 지도로 이동한다.
  const openMap = (appUrl: string, webUrl: string) => {
    if (deviceType === "web") {
      window.open(webUrl, "_blank", "noopener");
      return;
    }

    window.location.href = appUrl;
  };

  function openTmap(name: string) {
    const q = encodeURIComponent(name);
    openMap(`tmap://search?name=${q}`, "https://www.tmap.co.kr/");
  }
  function openKakaoMap(name: string) {
    const q = encodeURIComponent(name);
    openMap(`kakaomap://search?q=${q}`, `https://map.kakao.com/?q=${q}`);
  }

  // 주소 복사: 성공 시 Toast 안내
  const copyAddress = async (address: string) => {
    if (!address) {
      toast("복사할 주소가 없습니다.");
      return;
    }
    try {
      await navigator.clipboard?.writeText(address);
      toast("주소가 복사되었습니다.");
    } catch {
      toast("주소 복사에 실패했습니다.");
    }
  };

  // 입금계좌 복사
  const copyAccount = async (accountNumber: string) => {
    if (!accountNumber) return;
    try {
      await navigator.clipboard?.writeText(accountNumber);
      toast("복사되었습니다.");
    } catch {
      toast("복사에 실패했습니다.");
    }
  };

  // 접수 수정: add 플로우처럼 화면(프레임)을 채우는 풀 팝업으로 표시
  if (editOpen && tournamentInfo?.data) {
    return (
      <CompetitionDrawer
        id={id as string}
        type="edit"
        fullPopup
        open
        item={tournamentInfo.data as unknown as TournamentDetailResponse}
        calendarList={tournament?.calendarList || []}
        onClose={() => {
          setEditOpen(false);
          refetch();
        }}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#f2f2f7]">
      <AppBar
        backButton
        transparent
        hideBottomBorder
        className="bg-[#f2f2f7]"
      />

      <div
        className={`flex flex-col gap-[12px] px-[12px] py-[20px] ${
          hasBottomAction ? "pb-[100px]" : "pb-[40px]"
        }`}
      >
        {paymentInfo?.moid && (
          <div className="self-start bg-[#d1d1d6] rounded-full px-[12px] py-[8px]">
            <p className="text-sm font-medium text-black whitespace-pre-line break-all">
              거래번호 {paymentInfo?.moid}
            </p>
          </div>
        )}

        {/* 입금대기중 안내 (가상계좌/무통장입금 결제대기) */}
        {showDepositGuide && (
          <div className="bg-white rounded-[10px] p-[20px] flex flex-col items-center gap-[12px]">
            <div className="bg-[#ff3b30] size-12 rounded-full flex items-center justify-center">
              <span className="text-white text-3xl font-bold leading-none">
                !
              </span>
            </div>
            <div className="flex flex-col items-center gap-[4px] w-full text-center">
              <p className="text-xl font-medium text-black">입금대기중</p>
              <p className="text-base font-semibold text-[#8e8e93] break-keep whitespace-pre-line">
                {depositDeadlineShort ? (
                  <>
                    <span className="text-black">{depositDeadlineShort}</span>
                    까지 아래 계좌로 입금해 주세요.
                    <br />
                    입금기한까지 입금이 완료되지 않으면 접수가 자동으로
                    취소됩니다.
                    <br />
                    <span className="text-[#ff3b30] underline">
                      입금 전 대회 접수 취소는 불가능합니다.
                    </span>
                    <br />
                    <span className="text-[#ff3b30]">
                      입금 확인까지 10분 정도 소요될 수 있습니다.
                    </span>
                  </>
                ) : (
                  "입금 확인 후 순차적으로 승인 처리됩니다."
                )}
              </p>
            </div>

            <div className="w-full h-px bg-[#e5e5ea]" />

            {/* 입금 계좌 */}
            {depositAccounts.map((account, idx) => (
              <div
                key={`${account.accountNumber}-${idx}`}
                className="w-full bg-[#f2f2f7] rounded-[10px] p-[8px] flex flex-col gap-[8px]"
              >
                <p className="text-sm font-semibold text-black">
                  {isVbank ? "가상계좌" : account?.label}
                </p>
                <InfoRow
                  label="입금계좌"
                  value={
                    <div className="flex gap-[8px] items-center flex-wrap">
                      <span className="break-all">
                        {account.bankName} {account.accountNumber}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyAccount(account.accountNumber)}
                        className="shrink-0 py-[2px] px-[4px] rounded-[5px] border border-[#8e8e93] bg-white text-xs font-medium text-black"
                      >
                        복사
                      </button>
                    </div>
                  }
                />
                <InfoRow label="예금주명" value={account.accountHolder} />
                {tournament?.depositConfirmInfo && (
                  <InfoRow
                    label="입금문의"
                    value={tournament.depositConfirmInfo}
                  />
                )}
                {isVbank && paymentInfo?.vBankExpDt && (
                  <InfoRow label="입금기한" value={depositDeadline} />
                )}
              </div>
            ))}

            {/* 입금금액 */}
            <div className="w-full bg-[#f2f2f7] rounded-[10px] p-[12px] flex items-center justify-between">
              <p className="text-sm font-semibold text-black">입금금액</p>
              <p className="text-base font-semibold text-[#007aff]">
                {depositAmount.toLocaleString()}원
              </p>
            </div>
          </div>
        )}

        {/* 접수 정보 */}
        <SectionCard
          title="접수 정보"
          titleRight={
            (apply?.id ?? applyId) != null && (
              <span className="bg-[#f2f2f7] rounded-full px-[8px] py-[2px] text-sm font-medium text-black whitespace-nowrap">
                No.{apply?.id ?? applyId}
              </span>
            )
          }
        >
          <div className="flex flex-col gap-[8px] -mt-[8px]">
            <InfoRow
              label="접수상태"
              value={
                <Badge
                  label={stamp.label ?? ""}
                  className={stamp.className}
                  hasCheck={stamp.hasCheck}
                />
              }
            />
            <InfoRow label="접수일시" value={applyDateTime} />
            {/* 접수취소 상태: 취소유형·취소사유·취소일시 노출 */}
            {isCanceled && (
              <>
                <InfoRow
                  label="취소유형"
                  value={
                    <span className="font-semibold">
                      {apply?.cancelLabel || "-"}
                    </span>
                  }
                />
                <InfoRow label="취소사유" value={cancelReason} />
                <InfoRow label="취소일시" value={canceledDateTime} />
              </>
            )}
          </div>

          {/* QR 사용 대회에서 해당 일정/라운드가 시작된 경우에만 노출한다. */}
          {canQueryQrList && qrList.length > 0 && (
            <div className="grid w-full grid-cols-2 gap-[8px]">
              {qrList.map((qr, index) => (
                <Button
                  key={qr.qrId}
                  className={`font-normal rounded-[8px] p-[12px] text-base ${
                    qrList.length % 2 === 1 && index === qrList.length - 1
                      ? "col-span-2"
                      : ""
                  }`}
                  variant="black"
                  label={getQrButtonLabel(qr.qrLabel)}
                  disabled={qrCodeMutation.isPending}
                  onClick={() => openQrModal(qr)}
                />
              ))}
            </div>
          )}
        </SectionCard>

        {/* 신청 대회 정보 */}
        <SectionCard title="신청 대회 정보">
          <div className="flex flex-col gap-[8px] -mt-[8px]">
            <InfoRow label="대회명" value={tournament?.name || "-"} />
            {/* 라운드 대회는 여러 날에 걸치므로 대회 전체 기간, 일정 대회는 신청한 날짜 */}
            <InfoRow
              label="대회날짜"
              value={
                isRoundTournament
                  ? formatDateRange(
                      tournament?.startDt as string,
                      tournament?.endDt as string,
                      "YYYY-MM-DD (ddd)",
                    )
                  : tournament?.calendarInfo?.date
                    ? formatDate(
                        tournament.calendarInfo.date,
                        "YYYY-MM-DD (ddd)",
                      )
                    : "-"
              }
            />
            <InfoRow
              label="대회일정"
              value={tournament?.calendarInfo?.timeType || "-"}
            />
            <InfoRow label="대회장소" value={tournament?.golfParkName || "-"} />

            {/* 대회 장소 상세 정보 (접수취소 시 미노출) */}
            {!isCanceled && (
              <>
                {/* 장소 카드 */}
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[10px] flex gap-[12px] items-start">
                  <img
                    src={
                      tournament?.golfParkImageUrl
                        ? staticURL(tournament?.golfParkImageUrl)
                        : "/images/img-login-logo.webp"
                    }
                    alt="골프장 이미지"
                    className="size-[106px] rounded-[10px] object-cover shrink-0 bg-[#e3e3e3]"
                  />
                  <div className="flex-1 min-w-0 flex flex-col gap-[8px] justify-center self-stretch">
                    <p className="text-sm font-medium text-black break-all whitespace-pre-line">
                      {tournament?.golfParkName || "-"}
                    </p>
                    <div className="flex gap-[4px] items-start">
                      <img
                        src="/icons/ic-place-gray.webp"
                        alt="위치"
                        className="w-4 mt-[0.125rem] shrink-0"
                      />
                      <p className="flex-1 text-sm font-medium text-[#8e8e93] break-all">
                        {tournament?.address || "-"}
                      </p>
                    </div>
                    <button
                      onClick={() => copyAddress(tournament?.address || "")}
                      className="w-full bg-white border border-[#e5e5ea] rounded-[10px] py-[4px] text-xs font-medium text-[#8e8e93]"
                    >
                      주소 복사
                    </button>
                  </div>
                </div>

                {/* 티맵 / 카카오맵 */}
                <div className="flex gap-[8px] items-stretch">
                  {deviceType !== "web" && (
                    <button
                      onClick={() =>
                        openTmap(
                          tournament?.address || tournament?.golfParkName || "",
                        )
                      }
                      className="flex-1 border border-[#e5e5ea] rounded-[10px] py-[4px] flex items-center justify-center gap-[10px] text-xs font-medium text-black"
                    >
                      <img
                        src="/icons/ic-tmap.webp"
                        alt="티맵"
                        className="size-6"
                      />
                      티맵
                    </button>
                  )}
                  <button
                    onClick={() =>
                      openKakaoMap(
                        tournament?.address || tournament?.golfParkName || "",
                      )
                    }
                    className="flex-1 border border-[#e5e5ea] rounded-[10px] py-[4px] flex items-center justify-center gap-[10px] text-xs font-medium text-black"
                  >
                    <img
                      src="/icons/ic-kakao.webp"
                      alt="카카오맵"
                      className="size-6"
                    />
                    카카오맵
                  </button>
                </div>
              </>
            )}
          </div>

          {/* 대회 상세 보기 */}
          <Button
            type="button"
            className="w-full font-normal py-[12px]"
            variant="white"
            label="대회 상세 보기"
            onClick={() => {
              navigate(`/competition/${tournament?.id}`);
            }}
          />
        </SectionCard>

        {/* 내 신청 정보 + 조 편성 결과 */}
        <SectionCard>
          <div className="flex flex-col gap-[12px]">
            <p className="text-base font-semibold text-black">내 신청 정보</p>
            <div className="flex flex-col gap-[8px]">
              <InfoRow label="이름" value={user?.name || "-"} />
              <InfoRow
                label="생년월일"
                value={
                  user?.birth
                    ? parseBirth(user.birth).format("YYYY년 M월 D일")
                    : "-"
                }
              />
              <InfoRow
                label="성별"
                value={user?.gender === 1 ? "남성" : "여성"}
              />
              <InfoRow label="이메일" value={apply?.email || "-"} />
              {collectRegion && <InfoRow label="지역" value={region || "-"} />}
              {collectCertificate && (
                <InfoRow
                  label="프로자격증"
                  value={apply?.haveProCertificate ? "보유" : "미보유"}
                />
              )}
              {collectClass && (
                <InfoRow label="클래스" value={classLabel || "-"} />
              )}
            </div>
          </div>

          {/* 조 편성 결과 (접수취소 시 미노출) */}
          {!isCanceled && assignments.length > 0 && (
            <div className="flex flex-col gap-[12px]">
              <p className="text-base font-semibold text-black">조 편성 결과</p>
              {assignments.map((a, index) => (
                <div
                  key={index}
                  className="bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[4px]"
                >
                  <InfoRow
                    label="대회날짜"
                    value={
                      a.date ? formatDate(a.date, "YYYY년 M월 D일 (ddd)") : "-"
                    }
                  />
                  {/* 라운드 대회는 라운드명, 일정 대회는 대회일정 표시 */}
                  {isRoundTournament ? (
                    <InfoRow label="라운드명" value={a.roundName} />
                  ) : (
                    <InfoRow
                      label="대회일정"
                      value={tournament?.calendarInfo?.timeType || "-"}
                    />
                  )}
                  <InfoRow label="그룹" value={a.groupName} />
                  <InfoRow label="조" value={a.teamName} />
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* 결제 정보 (결제 전 접수취소 시 미노출) */}
        {showPaymentSection && (
          <SectionCard title="결제 정보">
            <div className="flex flex-col gap-[8px] -mt-[8px]">
              <InfoRow
                label="참가비"
                value={`${tournament?.entryFee?.toLocaleString()}원`}
              />
              <InfoRow
                label="결제상태"
                value={(() => {
                  const badge = paymentBadge(
                    paymentInfo?.paymentStatusLabel ??
                      (isPaid ? "결제완료" : "결제대기"),
                  );
                  return (
                    <Badge label={badge.label} className={badge.className} />
                  );
                })()}
              />
              <InfoRow
                label="결제수단"
                value={
                  paymentInfo?.payMethodLabel ??
                  (isOnline ? "신용카드" : "무통장입금")
                }
              />

              {/* 결제 완료 상세 */}
              {hasPaidDetail && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[8px]">
                  <p className="text-base font-semibold text-black">
                    결제 완료
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    {isCard && paymentInfo?.cardName && (
                      <>
                        <InfoRow
                          label="카드사"
                          value={`${paymentInfo.cardName}카드`}
                        />
                        {paymentInfo?.cardNo && (
                          <InfoRow
                            label="카드번호"
                            value={paymentInfo.cardNo}
                          />
                        )}
                        {paymentInfo?.cardQuotaLabel && (
                          <InfoRow
                            label="할부구분"
                            value={paymentInfo.cardQuotaLabel}
                          />
                        )}
                      </>
                    )}
                    <InfoRow
                      label="결제금액"
                      value={`${(paymentInfo?.amt ?? 0).toLocaleString()}원`}
                    />
                    <InfoRow
                      label="결제완료일시"
                      value={
                        paidAt
                          ? formatDate(paidAt, "YYYY-MM-DD (ddd) HH:mm:ss")
                          : "-"
                      }
                    />
                  </div>
                </div>
              )}

              {/* 결제 대기(가상계좌) → 참가비 입금 계좌 안내 */}
              {isVbankWait && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[8px]">
                  <p className="text-base font-semibold text-black">
                    참가비 입금 계좌
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    <InfoRow
                      label="입금계좌"
                      value={`${paymentInfo?.vBankBankName} ${paymentInfo?.vBankNum}`}
                    />
                    <InfoRow
                      label="예금주명"
                      value={paymentInfo?.vBankAccountHolder || "-"}
                    />
                    <InfoRow
                      label="입금금액"
                      value={`${depositAmount.toLocaleString()}원`}
                    />
                    <InfoRow label="입금기한" value={depositDeadline} />
                  </div>
                </div>
              )}

              {/* 결제 실패 */}
              {isPaymentFailed && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[8px]">
                  <p className="text-base font-semibold text-black">
                    {paymentInfo?.paymentStatusLabel === "결제실패"
                      ? "결제 실패"
                      : "결제 미완료"}
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    {isCard ? (
                      <>
                        <InfoRow
                          label="카드사"
                          value={
                            paymentInfo?.cardName
                              ? `${paymentInfo.cardName}카드`
                              : "-"
                          }
                        />
                        {paymentInfo?.cardNo && (
                          <InfoRow
                            label="카드번호"
                            value={paymentInfo.cardNo}
                          />
                        )}
                        <InfoRow
                          label="결제요청금액"
                          value={`${depositAmount.toLocaleString()}원`}
                        />
                      </>
                    ) : (
                      <InfoRow
                        label="결제예정금액"
                        value={`${depositAmount.toLocaleString()}원`}
                      />
                    )}
                    <InfoRow
                      label={
                        paymentInfo?.paymentStatusLabel === "결제실패"
                          ? "결제실패사유"
                          : "결제미완료사유"
                      }
                      value={paymentInfo?.failReason || "-"}
                    />
                    <InfoRow
                      label={
                        paymentInfo?.paymentStatusLabel === "결제실패"
                          ? "결제실패일시"
                          : "결제미완료일시"
                      }
                      value={
                        failedAt
                          ? formatDate(failedAt, "YYYY-MM-DD (ddd) HH:mm:ss")
                          : "-"
                      }
                    />
                    {/* <InfoRow label="결제마감일시" value={depositDeadline} /> */}
                  </div>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* 환불 정보 (환불 이력이 있을 때만 노출) */}
        {hasRefund && (
          <SectionCard title="환불 정보">
            <div className="flex flex-col gap-[8px] -mt-[8px]">
              <InfoRow
                label="환불예정금액"
                value={`${(paymentInfo?.refundAmt ?? 0).toLocaleString()}원`}
              />
              <InfoRow
                label="환불상태"
                value={
                  <Badge
                    label={paymentInfo?.refundStatusLabel || "환불대기"}
                    className={
                      isRefundFailed
                        ? "bg-[#ffecec] text-[#ff3b30]"
                        : isRefundDone
                          ? "bg-[#f2ffec] text-[#34c759]"
                          : "bg-[#fff4d8] text-[#a2845e]"
                    }
                  />
                }
              />
              <InfoRow
                label="환불수단"
                value={paymentInfo?.payMethodLabel || "-"}
              />
              <InfoRow
                label="환불요청일시"
                value={apply?.cancelDt ? canceledDateTime : "-"}
              />

              {/* 환불 완료 */}
              {isRefundDone && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[8px]">
                  <p className="text-base font-semibold text-black">
                    환불 완료
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    {isCard ? (
                      <>
                        <InfoRow
                          label="카드사"
                          value={
                            paymentInfo?.cardName
                              ? `${paymentInfo.cardName}카드`
                              : "-"
                          }
                        />
                        {paymentInfo?.cardNo && (
                          <InfoRow
                            label="카드번호"
                            value={paymentInfo.cardNo}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <InfoRow label="환불계좌" value={refundAccount} />
                        <InfoRow
                          label="예금주명"
                          value={maskHolder(paymentInfo?.refundAccountHolder)}
                        />
                      </>
                    )}
                    <InfoRow
                      label="환불금액"
                      value={`${(paymentInfo?.refundAmt ?? 0).toLocaleString()}원`}
                    />
                    <InfoRow
                      label="환불완료일시"
                      value={
                        paymentInfo?.refundAt
                          ? formatDate(
                              paymentInfo.refundAt,
                              "YYYY-MM-DD (ddd) HH:mm:ss",
                            )
                          : "-"
                      }
                    />
                  </div>
                </div>
              )}

              {/* 환불 실패 */}
              {isRefundFailed && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] p-[12px] flex flex-col gap-[8px]">
                  <p className="text-base font-semibold text-black">
                    환불 실패
                  </p>
                  <div className="flex flex-col gap-[4px]">
                    {isCard ? (
                      <>
                        <InfoRow
                          label="카드사"
                          value={
                            paymentInfo?.cardName
                              ? `${paymentInfo.cardName}카드`
                              : "-"
                          }
                        />
                        {paymentInfo?.cardNo && (
                          <InfoRow
                            label="카드번호"
                            value={paymentInfo.cardNo}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <InfoRow label="환불계좌" value={refundAccount} />
                        <InfoRow
                          label="예금주명"
                          value={maskHolder(paymentInfo?.refundAccountHolder)}
                        />
                      </>
                    )}
                    <InfoRow
                      label="환불요청금액"
                      value={`${(paymentInfo?.refundAmt ?? 0).toLocaleString()}원`}
                    />
                    <InfoRow
                      label="환불실패사유"
                      value={paymentInfo?.refundFailReason || "-"}
                    />
                    <InfoRow
                      label="환불실패일시"
                      value={
                        paymentInfo?.refundFailedAt
                          ? formatDate(
                              paymentInfo.refundFailedAt,
                              "YYYY-MM-DD (ddd) HH:mm:ss",
                            )
                          : "-"
                      }
                    />
                  </div>
                </div>
              )}

              {/* 환불 안내 (결제수단별 문구 상이, 환불 실패 시 미노출) */}
              {!isRefundFailed && (
                <div className="mt-[4px] bg-[#f2f2f7] rounded-[10px] px-[12px] py-[8px]">
                  <p className="text-xs font-medium text-[#8e8e93] break-keep">
                    {isCard
                      ? "카드사 정책에 따라 환불 완료까지 영업일 기준 3~5일 정도 소요될 수 있습니다."
                      : "입력된 환불 계좌로 환불이 진행됩니다. 환불 완료까지 영업일 기준 3~5일 정도 소요될 수 있습니다."}
                  </p>
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>

      {/* 하단 고정 액션 (표시할 버튼이 있을 때만 노출) */}
      {hasBottomAction && (
        <div className="bg-white p-[12px] fixed max-w-[440px] w-full bottom-0 z-50 flex gap-[8px]">
          {canCancelApply || canEditApply ? (
            // 접수 취소 / 접수 수정 (접수 기간 내)
            <>
              {canCancelApply && (
                <Button
                  type="button"
                  className="flex-1 font-normal text-base"
                  variant="black"
                  label="접수 취소"
                  onClick={handleCancelApply}
                />
              )}
              {canEditApply && (
                <Button
                  type="button"
                  className="flex-1 font-normal text-base"
                  variant="black"
                  label="접수 수정"
                  onClick={handleEditApply}
                />
              )}
            </>
          ) : (
            // 참가완료 또는 대회종료·결제완료: 대회 순위 보기
            <Button
              type="button"
              className="w-full font-normal text-base"
              variant="black"
              label="대회 순위 보기"
              onClick={() =>
                navigate(`/score/${tournamentInfo?.data.scoreCardId}`)
              }
            />
          )}
        </div>
      )}

      {/* 접수 수정 (풀 팝업 · 약관 다음 단계부터 · 기존 정보 프리필) */}
      {editOpen && tournamentInfo?.data && (
        <div className="fixed inset-0 z-[60] bg-black/40">
          <div className="absolute inset-0 mx-auto max-w-[440px] bg-white overflow-y-auto">
            <CompetitionDrawer
              id={id as string}
              type="edit"
              fullPopup
              open={editOpen}
              item={tournamentInfo.data as unknown as TournamentDetailResponse}
              calendarList={tournament?.calendarList || []}
              onClose={() => {
                setEditOpen(false);
                refetch();
              }}
            />
          </div>
        </div>
      )}

      {/* 현장 인증 QR.
       * 화면 기준으로 띄우면 데스크톱에서 앱 프레임과 어긋나므로,
       * 프레임(포지션 컨테이너) 안으로 포털을 보내 정확히 겹치게 한다. */}
      {qrModal &&
        createPortal(
          <div className="absolute inset-0 z-[100] flex flex-col bg-white">
            <TournamentQrModal
              title={qrModal.title}
              code={qrModal.code}
              rows={qrInfoRows}
              isChecking={isCheckingQr}
              close={closeQrModal}
            />
          </div>,
          document.querySelector("main")?.parentElement ?? document.body,
        )}

      {/* 인증 완료 화면 (QR 화면을 닫을 때 인증이 확인된 경우) */}
      {qrResultTitle &&
        createPortal(
          <div className="absolute inset-0 z-[100] flex flex-col">
            <TournamentQrResultModal
              title={qrResultTitle}
              rows={qrInfoRows}
              close={() => setQrResultTitle(null)}
            />
          </div>,
          document.querySelector("main")?.parentElement ?? document.body,
        )}
    </div>
  );
}
